import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { postBoostCampaignRouteFactory } from "./postBoostCampaign";
import { adOpsCampaignRouteFactory } from "./adOpsCampaign";
import { audienceRouteFactory } from "./audience";
import { setupTemplateRouteFactory } from "./setupTemplate";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { adAccountViewLoader, adAccountFormLoader } from "../loaders/adAccount";
import { postBoostCampaignListByAdAccountLoader } from "../loaders/postBoostCampaign";
import { adOpsCampaignListByAdAccountLoader } from "../loaders/adOpsCampaign";
import { audienceListByAdAccountLoader } from "../loaders/audience";
import { setupTemplateListByAdAccountLoader } from "../loaders/setupTemplate";
import { AdAccountBreadcrumb } from "../../breadcrumbs/adAccount";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { ADACCOUNT, POSTBOOSTCAMPAIGN, ADOPSCAMPAIGN, AUDIENCE, SETUPTEMPLATE } = ENTITY;

export const adAccountRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const adAccountRoute = createRoute({
    getParentRoute: () => parent,
    path: ADACCOUNT.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADACCOUNT.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADACCOUNT.label.upFirstPlural,
    },
  });

  // List adAccounts

  const adAccountListRoute = createRoute({
    getParentRoute: () => adAccountRoute,
    path: "/",
    pendingComponent: () => <EntityListLoading entity={ADACCOUNT} />,
  }).lazy(() => import("./adAccount.lazy").then((d) => d.AdAccountListRoute));

  // View adAccount

  const adAccountViewRootRoute = createRoute({
    getParentRoute: () => adAccountRoute,
    path: "$adAccountId",
    staticData: {
      breadcrumb: AdAccountBreadcrumb,
    },
    loader: adAccountViewLoader,
  });

  // Edit adAccount

  const adAccountEditRoute = createRoute({
    getParentRoute: () => adAccountViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: adAccountFormLoader,
    pendingComponent: () => <EntityEditLoading entity={ADACCOUNT} />,
  }).lazy(() => import("./adAccount.lazy").then((d) => d.AdAccountEditRoute));

  // View adAccount Layout

  const adAccountViewLayoutRoute = createRoute({
    getParentRoute: () => adAccountViewRootRoute,
    id: "adAccount-view-layout",
    pendingComponent: () => <EntityViewLoading entity={ADACCOUNT} />,
  }).lazy(() =>
    import("./adAccount.lazy").then((d) => d.AdAccountViewLayoutRoute)
  );

  // View adAccount - General Tab

  const adAccountViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then((d) => d.AdAccountViewLayoutGeneralTabRoute)
  );

  // View adAccount - About Tab

  const adAccountViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then((d) => d.AdAccountViewLayoutAboutTabRoute)
  );

  // View adAccount - PostBoostCampaigns Tab

  const adAccountViewLayoutPostBoostCampaignListTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: `${POSTBOOSTCAMPAIGN.type.plural}/`,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: POSTBOOSTCAMPAIGN.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: POSTBOOSTCAMPAIGN.label.upFirstPlural,
    },
    loader: postBoostCampaignListByAdAccountLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then(
      (d) => d.AdAccountViewLayoutPostBoostCampaignListTabRoute
    )
  );

  // View adAccount - AdOpsCampaigns Tab

  const adAccountViewLayoutAdOpsCampaignListTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: `${ADOPSCAMPAIGN.type.plural}/`,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: ADOPSCAMPAIGN.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: ADOPSCAMPAIGN.label.upFirstPlural,
    },
    loader: adOpsCampaignListByAdAccountLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then(
      (d) => d.AdAccountViewLayoutAdOpsCampaignListTabRoute
    )
  );

   // View adAccount - Audiences Tab

   const adAccountViewLayoutAudienceListTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: `${AUDIENCE.type.plural}/`,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: AUDIENCE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: AUDIENCE.label.upFirstPlural,
    },
    loader: audienceListByAdAccountLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then(
      (d) => d.AdAccountViewLayoutAudienceListTabRoute
    )
  );

  // View adAccount - SetupTemplates Tab

  const adAccountViewLayoutSetupTemplateListTabRoute = createRoute({
    getParentRoute: () => adAccountViewLayoutRoute,
    path: `${SETUPTEMPLATE.type.plural}/`,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: SETUPTEMPLATE.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: SETUPTEMPLATE.label.upFirstPlural,
    },
    loader: setupTemplateListByAdAccountLoader,
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./adAccount.lazy").then(
      (d) => d.AdAccountViewLayoutSetupTemplateListTabRoute
    )
  );

  // AdAccount Children
  const postBoostCampaignRoute = postBoostCampaignRouteFactory(
    adAccountViewRootRoute
  );
  const adOpsCampaignRoute = adOpsCampaignRouteFactory(adAccountViewRootRoute);
  const audienceRoute = audienceRouteFactory(adAccountViewRootRoute);
  const setupTemplateRoute = setupTemplateRouteFactory(adAccountViewRootRoute);

  // Route tree
  adAccountRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [adAccountListRoute]),
    adAccountViewRootRoute.addChildren([
      adAccountEditRoute,
      adAccountViewLayoutRoute.addChildren([
        adAccountViewLayoutGeneralTabRoute,
        adAccountViewLayoutAboutTabRoute,
        adAccountViewLayoutPostBoostCampaignListTabRoute,
        adAccountViewLayoutAdOpsCampaignListTabRoute,
        adAccountViewLayoutAudienceListTabRoute,
        adAccountViewLayoutSetupTemplateListTabRoute
      ]),
      postBoostCampaignRoute,
      adOpsCampaignRoute,
      audienceRoute,
      setupTemplateRoute
    ]),
  ]);

  return adAccountRoute;
};

const adAccountRoute = adAccountRouteFactory();

export default adAccountRoute;
